import { useEffect, useRef, useState } from 'react';
import { FiFileText, FiPlus, FiRotateCcw, FiX } from 'react-icons/fi';
import { getAttachmentName, getAttachmentUrl, isPdfAttachment } from '../../utils/attachments';
import { AuthenticatedAttachmentImage } from '../../hooks/useAuthenticatedAttachmentUrl';
import { AppTooltip } from '../ui/AppTooltip';

const NewFilePreview = ({ file }) => {
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (!file.type.startsWith('image/')) return undefined;
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  if (!url) return <FiFileText className='h-6 w-6 text-[var(--brand)]' />;
  return <img src={url} alt='' className='h-full w-full object-cover' />;
};

const AttachmentTile = ({ name, onRemove, children, removeLabel }) => (
  <div className='group relative h-16 w-16 shrink-0 overflow-hidden rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-panel-muted)] shadow-sm'>
    <div className='flex h-full w-full items-center justify-center'>{children}</div>
    <div className='pointer-events-none absolute inset-x-0 bottom-0 truncate bg-slate-950/75 px-1.5 py-1 text-[10px] font-medium text-white'>{name}</div>
    <AppTooltip label={removeLabel}><button
      type='button'
      onClick={onRemove}
      className='absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full border border-white/20 bg-slate-950/85 text-white shadow transition hover:bg-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]'
      aria-label={removeLabel}
    >
      <FiX className='h-4 w-4' />
    </button></AppTooltip>
  </div>
);

export const TransactionAttachmentField = ({
  existingAttachments,
  retainedAttachments,
  newFiles,
  onRemoveExisting,
  onRemoveNew,
  onAddFiles,
  onRestore,
  t,
  maxFiles = 25,
}) => {
  const inputRef = useRef(null);
  const total = retainedAttachments.length + newFiles.length;
  const removedCount = existingAttachments.length - retainedAttachments.length;

  const handleChange = (event) => {
    onAddFiles(Array.from(event.target.files || []));
    event.target.value = '';
  };

  return (
    <section className='rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-panel-muted)] p-3'>
      <div className='mb-2 flex flex-wrap items-center justify-between gap-2'>
        <div>
          <h3 className='text-sm font-semibold text-[var(--text-primary)]'>{t('Attachments')}</h3>
          <p className='mt-0.5 text-xs text-[var(--text-muted)]'>{t('Accepted formats: JPG, PNG, WEBP, JFIF, PDF.')}</p>
        </div>
        <div className='flex items-center gap-3 text-xs text-[var(--text-muted)]'>
          {removedCount > 0 && (
            <button type='button' onClick={onRestore} className='inline-flex items-center gap-1 font-semibold text-[var(--brand-strong)] hover:underline'>
              <FiRotateCcw /> {t('Restore attachments')}
            </button>
          )}
          <span>{total} / {maxFiles}</span>
        </div>
      </div>

      <div className='flex min-h-16 flex-wrap gap-2'>
        {retainedAttachments.map((attachment, index) => {
          const name = getAttachmentName(attachment);
          return (
            <AttachmentTile key={`${getAttachmentUrl(attachment)}-${index}`} name={name} onRemove={() => onRemoveExisting(attachment)} removeLabel={`${t('Remove')} ${name}`}>
              {isPdfAttachment(attachment) ? <FiFileText className='h-6 w-6 text-[var(--brand)]' /> : <AuthenticatedAttachmentImage attachment={attachment} alt={name} className='h-full w-full object-cover' />}
            </AttachmentTile>
          );
        })}

        {newFiles.map((file, index) => (
          <AttachmentTile key={`${file.name}-${file.lastModified}-${index}`} name={file.name} onRemove={() => onRemoveNew(index)} removeLabel={`${t('Remove')} ${file.name}`}>
            <NewFilePreview file={file} />
          </AttachmentTile>
        ))}

        {total < maxFiles && (
          <button
            type='button'
            onClick={() => inputRef.current?.click()}
            className='flex h-16 w-16 shrink-0 flex-col items-center justify-center gap-0.5 rounded-[var(--radius-md)] border border-dashed border-[var(--border-strong)] bg-[var(--surface-panel)] text-[var(--text-muted)] transition hover:border-[var(--brand)] hover:bg-[var(--brand-soft)] hover:text-[var(--brand-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]'
            aria-label={t('Add attachments')}
          >
            <FiPlus className='h-5 w-5' />
            <span className='text-[10px] font-semibold'>{t('Add files')}</span>
          </button>
        )}
      </div>

      <input ref={inputRef} type='file' multiple accept='.jpg,.jpeg,.png,.webp,.jfif,.pdf' className='sr-only' onChange={handleChange} />
    </section>
  );
};
